import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import useDebounce from 'helpers/debounce';
import { ICustomerReference } from 'models/User';

const MIN_SEARCH_LENGTH = 3;

const CustomerMultiSelect = (props: Props) => {
  const { t } = useTranslation();
  const [searchResults, setSearchResults] = useState<ICustomerReference[]>([]);
  const [value, setValue] = useState<ICustomerReference | undefined>(props.value);
  const [loading, setLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [searchText, setSearchText] = useState('');

  const debouncedSearchText = useDebounce(searchText);

  const isServerSearch = !props.options;

  useEffect(() => {
    if (!props.searchCustomers) return;

    if (debouncedSearchText.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      setSearchFailed(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    props
      .searchCustomers(debouncedSearchText)
      .then((results) => {
        if (cancelled) return;
        setSearchResults(results);
        setSearchFailed(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error(error);
        setSearchResults([]);
        setSearchFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearchText]);

  const noOptionsMessage = () => {
    if (!isServerSearch) return t('profile.NO_CUSTOMERS_FOUND');
    if (searchFailed) return t('profile.CUSTOMER_SEARCH_FAILED');
    if (searchText.trim().length < MIN_SEARCH_LENGTH)
      return t('profile.CUSTOMER_SEARCH_MIN_LENGTH', { minLength: MIN_SEARCH_LENGTH });

    return t('profile.NO_CUSTOMERS_FOUND');
  };

  return (
    <Select<ICustomerReference, false>
      getOptionLabel={(item: ICustomerReference) => item.name}
      getOptionValue={(item: ICustomerReference) => item.id.toString()}
      placeholder={
        isServerSearch ? t('profile.ENTER_CUSTOMER_NAME') : t('profile.SELECT_ACTIVE_CUSTOMER')
      }
      classNamePrefix="drop-"
      name="organizationUnit"
      value={value}
      onChange={(e) => {
        if (!e) return;
        setValue(e);
        props.onChange(e);
      }}
      options={props.options ?? searchResults}
      isLoading={loading}
      loadingMessage={() => t('profile.CUSTOMER_SEARCH_LOADING')}
      noOptionsMessage={noOptionsMessage}
      onInputChange={(newValue) => setSearchText(newValue)} // Track user input
    />
  );
};

interface Props {
  onChange: (value: ICustomerReference) => void;
  /** Full list of selectable customers. When set, filtering happens locally. */
  options?: ICustomerReference[];
  /** Server side search, used when the list is too large to load up front. */
  searchCustomers?: (searchText: string) => Promise<ICustomerReference[]>;
  value?: ICustomerReference;
}

export default CustomerMultiSelect;
